"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "./LanguageProvider";
import type { Car, CarsResponse } from "@/lib/cars";

const COUNT = 8;
const EASE = [0.22, 1, 0.36, 1] as const;

const formatPrice = (price: number) => `${price.toLocaleString("sv-SE")} kr`;

function CarCard({ car, index }: { car: Car; index: number }) {
  const { language } = useLanguage();
  const facts = [
    car.year ? String(car.year) : null,
    car.mileage != null ? `${car.mileage.toLocaleString("sv-SE")} mil` : null,
    car.fuel_type,
    car.transmission,
  ].filter(Boolean);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.08, ease: EASE }}
      className="w-[78vw] max-w-xs shrink-0 snap-start sm:w-auto sm:max-w-none"
    >
      <Link href={car.url || `/fordon-i-lager/${car.id}`} className="group block">
        <div className="relative mb-4 aspect-[4/3] overflow-hidden rounded-lg bg-neutral-100">
          {car.images[0] ? (
            <Image
              src={car.images[0]}
              alt={`${car.title} ${car.model_specification ?? ""}`.trim()}
              fill
              sizes="(max-width: 640px) 80vw, (max-width: 1024px) 50vw, 25vw"
              className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              unoptimized
            />
          ) : null}
          {car.is_campaign ? (
            <span className="absolute left-3 top-3 rounded-full bg-white px-3 py-1 text-xs font-semibold text-foreground">
              {language === "sv" ? "Kampanj" : "Offer"}
            </span>
          ) : null}
        </div>
        <h3 className="font-semibold text-foreground transition-colors group-hover:text-primary">{car.title}</h3>
        {car.model_specification ? (
          <p className="line-clamp-1 text-sm text-muted-foreground">{car.model_specification}</p>
        ) : null}
        <p className="mt-2 text-xs text-muted-foreground">{facts.join(" · ")}</p>
        <p className="mt-2 text-lg font-bold text-foreground">
          {car.price ? formatPrice(car.price) : language === "sv" ? "Pris på förfrågan" : "Price on request"}
        </p>
      </Link>
    </motion.div>
  );
}

/** The most recently published cars from Bilspektrum's Blocket stock. */
export function LatestCarsSection() {
  const { language } = useLanguage();
  const sv = language === "sv";
  const [cars, setCars] = useState<Car[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/cars?latest=${COUNT}`)
      .then((res) => (res.ok ? res.json() : { cars: [] }))
      .then((data: CarsResponse) => !cancelled && setCars(data.cars ?? []))
      .catch(() => !cancelled && setCars([]));
    return () => {
      cancelled = true;
    };
  }, []);

  // Nothing to show (Blocket down): hide the section rather than show an empty shell.
  if (cars && cars.length === 0) return null;

  return (
    <section className="border-t border-border bg-background py-20 lg:py-24">
      <div className="container mx-auto max-w-6xl px-6">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="mb-3 text-xs font-medium uppercase tracking-[0.25em] text-muted-foreground">
              {sv ? "Bilar i lager" : "Cars in stock"}
            </p>
            <h2 className="font-display text-3xl font-bold text-foreground lg:text-4xl">
              {sv ? "Nyinkomna bilar" : "Just arrived"}
            </h2>
          </div>
          <Link
            href="/fordon-i-lager"
            className="inline-flex items-center gap-2 rounded-full border border-foreground/20 px-5 py-2.5 text-sm font-semibold text-foreground transition hover:bg-foreground hover:text-background"
          >
            {sv ? "Se alla bilar i lager" : "See all cars in stock"}
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>
      </div>

      <div className="container mx-auto max-w-6xl sm:px-6">
        {cars === null ? (
          <div className="flex gap-6 overflow-hidden px-6 sm:grid sm:grid-cols-2 sm:px-0 lg:grid-cols-4">
            {Array.from({ length: 4 }, (_, i) => (
              <div key={i} className="w-[78vw] max-w-xs shrink-0 sm:w-auto sm:max-w-none">
                <div className="mb-4 aspect-[4/3] animate-pulse rounded-lg bg-neutral-100" />
                <div className="mb-2 h-4 w-2/3 animate-pulse rounded bg-neutral-100" />
                <div className="h-4 w-1/3 animate-pulse rounded bg-neutral-100" />
              </div>
            ))}
          </div>
        ) : (
          <div className="flex snap-x snap-mandatory gap-6 overflow-x-auto px-6 pb-2 sm:grid sm:grid-cols-2 sm:gap-x-6 sm:gap-y-10 sm:overflow-visible sm:px-0 lg:grid-cols-4">
            {cars.map((car, index) => (
              <CarCard key={car.id} car={car} index={index} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
